import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { SCROLL_DEBOUNCE_TIME, SCROLL_THROTTLE_TIME } from '../../../constants';
import { debounce, throttle } from 'lodash';
import utilities from './../../../utils/utilities';
import Spinner from '../../Spinner/Spinner.jsx';
import utils from './../../../utils/utilities';

export default function withInfiniteScroll(CollectionComponent) {
    class InfiniteScroll extends Component {
        static propTypes = {
            listItems: PropTypes.array,
            itemCount: PropTypes.number,
            loadItems: PropTypes.func,
            isFinished: PropTypes.bool,
            fullScreen: PropTypes.bool,
            withPromise: PropTypes.bool
        }

        static defaultProps = {
            isFinished: false,
            fullScreen: false,
            withPromise: true
        }

        constructor(props) {
            super(props);
            this.container = null;
            this.tableWrapper = null;
            this.table = null;
            this.listening = false;
            this.state = {
                fixedHeader: false,
                headerWidth: null,
                pendingRequest: false
            }
            this.updateHeaderWidth = this.updateHeaderWidth.bind(this);
            this.scrollEvent = this.scrollEvent.bind(this)
        }

        componentDidMount() {
            this.listenToScroll();
            this.updateHeaderWidth();
            this.scrollEvent();
            window.addEventListener('resize', this.updateHeaderWidth);
            this._isMounted = true;
        }

        componentDidUpdate() {
            this.listenToScroll();
        }

        componentWillReceiveProps() {
            // when props change, let's check if the scrolling position still in place
            this.loadOnScrollEvent();
        }

        componentWillUnmount() {
            if (this.listening && this.container) {
                this.container.removeEventListener('scroll', this.scrollEvent); // unmount event listener
            }
            window.removeEventListener('resize', this.updateHeaderWidth);
            this._isMounted = false;
        }

        updateHeaderWidth() {
            this.setState({headerWidth: this.table.getBoundingClientRect().width});
        }

        /**
         * Setter for pendingRequest in state
         * @param value
         * @returns {Scrollable}
         */
        setPendingRequest(value) {
            if (!this._isMounted) return;
            this.setState({ pendingRequest: value });
            return this;
        }

        /**
         * Adds listener to scroll event, adds the event to the appropriate container
         */
        listenToScroll() {
            if (!this.listening && this.tableWrapper) {
                if (this.props.fullScreen) {
                    this.container = window
                } else {
                    this.container = this.tableWrapper
                }
                this.container.addEventListener('scroll', this.scrollEvent);
                this.listening = true;
            }
        }

        /**
         * Throttled function to calculate fixed header position
         * @type {Function}
         */
        calculateHeaderPosition = throttle(() => {
            const isFixedHeader = !utils.isElementTopInViewport(this.table);
            if (this.props.fullScreen && this.state.fixedHeader !== isFixedHeader) {
                this.setState({ fixedHeader: isFixedHeader })
            }
        })

        /**
         * Debounced function called on scroll, loads more items
         * @type {function(this:Scrollable)}
         */
        loadOnScrollEvent = debounce(() => {
            if (this.container && this.table) {
                const height = this.props.fullScreen ?
                    ("innerHeight" in window ? window.innerHeight : document.documentElement.offsetHeight)
                    : this.container.offsetHeight
                const scrollTop = this.props.fullScreen ? window.pageYOffset : this.container.scrollTop;
                if (this.props.loadParams && this.props.loadParams.pagination) {
                    const isMore = this.props.itemCount >
                        (this.props.loadParams.pagination.pageNumber * this.props.loadParams.pagination.pageSize);

                    if (height + scrollTop > this.table.offsetHeight && isMore) {
                        this.loadItems({ pagination: { pageNumber: this.props.loadParams.pagination.pageNumber + 1}})
                    }
                } else {
                    this.loadItems();
                }
            }
        }, SCROLL_DEBOUNCE_TIME).bind(this)

        /**
         * Calls debounced function, sets fixed header state
         */
        scrollEvent() {
            this.loadOnScrollEvent();
            this.calculateHeaderPosition();
        }

        /**
         * General function for loading items
         * @param params
         */
        loadItems(params = null) {
            if (this.props.listItems && this.props.itemCount > this.props.listItems.length && !this.state.pendingRequest) {
                if (this.props.withPromise) {
                    this.setPendingRequest(true);
                    return this.props.loadItems(params).then(response => {
                        this.setPendingRequest(false);
                        return response;
                    })
                } else {
                    this.props.loadItems(params);
                }
            }
        }

        render() {
            return (
                <div className={"scrollable " + (!this.props.fullScreen ? "inner" : "")}>
                    <div className={"scrollable-table " + (this.props.fullScreen ? "full-screen" : "")} ref={(el) => this.tableWrapper = el }>
                        <div className="scrollable-wrap" ref={(el) => this.table = el }>
                            <CollectionComponent {...this.props}
                                                 className={this.state.fixedHeader ? "fixed" : ""}
                                                 headerWidth={this.state.headerWidth}/>
                        </div>
                        { this.props.listItems !== null &&
                        (this.props.itemCount > this.props.listItems.length && !this.props.isFinished) ? (
                            <div className="loading-row">
                                <Spinner isLoading={this.state.pendingRequest} />
                            </div>
                        ) : !this.props.isFinished && (this.props.listItems && this.props.listItems.length) && this.props.fullScreen ?
                            <div className="loading-row__placeholder" /> : null
                            // ^ this is a workaround, could be padding instead but on chrome the spinner flickers
                        }
                    </div>
                </div>
            )
        }
    }

    InfiniteScroll.displayName = `InfiniteScroll(${utilities.getDisplayName(CollectionComponent)})`;
    return InfiniteScroll;
}



// WEBPACK FOOTER //
// ./src/scripts/reusable/LightTable/hoc/Scrollable.jsx